import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import useLocationMock from './useLocationMock';
import locationStateAtom from './locationStateBridge';
import { _navActionRef, _wasPopNavigation } from './navStateBridge';
if (typeof window !== 'undefined') {
  // Prevent HMR from accumulating multiple listeners: clean up the previous
  // registration before adding a new one.
  if (window.__useHistoryMockCleanup__) {
    window.__useHistoryMockCleanup__();
  }
  const _popstateHandler = () => {
    _navActionRef.current = 'POP';
    _wasPopNavigation.current = true;
  };
  window.addEventListener('popstate', _popstateHandler);
  const _origPushState = window.history.pushState.bind(window.history);
  const _origReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = function (...args) {
    _navActionRef.current = 'PUSH';
    _wasPopNavigation.current = false;
    return _origPushState(...args);
  };
  window.history.replaceState = function (...args) {
    const prevAction = _navActionRef.current;
    _navActionRef.current = 'REPLACE';
    if (prevAction !== 'POP') {
      _wasPopNavigation.current = false;
    }
    return _origReplaceState(...args);
  };
  window.__useHistoryMockCleanup__ = () => {
    window.removeEventListener('popstate', _popstateHandler);
    window.history.pushState = _origPushState;
    window.history.replaceState = _origReplaceState;
  };
}

// Resolve a React Router v5 `to` argument (a string or a location-like object)
// against the current browser location into an absolute URL. Returns null on
// the server or when `to` cannot be resolved.
const resolveTargetUrl = to => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    if (typeof to === 'string') {
      return new URL(to, window.location.href);
    }
    if (to && typeof to === 'object') {
      const {
        pathname: toPathname,
        search,
        hash
      } = to;
      const searchWithPrefix = search && !search.startsWith('?') ? `?${search}` : search ?? '';
      return new URL(`${toPathname ?? window.location.pathname}${searchWithPrefix}${hash ?? ''}`, window.location.href);
    }
  } catch {
    // Fall through: an unparsable target takes the router path below.
  }
  return null;
};
const stripTrailingSlash = path => path.length > 1 ? path.replace(/\/+$/, '') : path;

// True when navigating to `target` would leave the user on the same route and
// only change the query string (same origin, same pathname ignoring the
// trailing slash, no hash to scroll to).
const isSameRouteQueryUpdate = target => Boolean(target) && target.origin === window.location.origin && stripTrailingSlash(target.pathname) === stripTrailingSlash(window.location.pathname) && !target.hash;

// React Router v5 treats `history.push/replace({ search })` as a pure
// client-side history operation. Translating it into `router.push/replace()`
// turns every filter or option change into a Next.js navigation, which fetches
// the route's RSC payload from the server. That round trip is redundant here
// (no server component reads `searchParams`; the client already has the new
// query via `useSearchParams`) and it is a failure surface: a failed or errored
// RSC response makes Next fall back to a full browser navigation, wiping the
// page's client state, or surface the error in the route's error boundary.
// This is the mechanism behind the intermittent swatches page crash that forced
// the previous Next.js 16.2 upgrade to be reverted (CON-8260 / CON-8235).
//
// Next.js integrates native `window.history.pushState/replaceState` calls into
// the App Router (it copies its internal tree into the new entry and updates
// `usePathname`/`useSearchParams`), so same-route query updates go through the
// native API instead - the same approach `useCategoryPageURL` and the
// inspiration grid already use. Cross-route navigations and hash targets still
// go through the router so Next's scroll-to-hash and route loading are kept.
// (CON-8606)
const applySameRouteQueryUpdate = (method, target) => {
  const href = `${target.pathname}${target.search}`;
  window.history[method](null, '', href);
};
const useHistoryMock = () => {
  const history = useRef({});
  const location = useLocationMock();
  history.current.location = location;
  const {
    pathname
  } = location || {};
  const {
    back,
    push,
    replace
  } = useRouter();
  history.current.back = back;
  const setLocationState = useSetAtom(locationStateAtom);

  // Wrap Next.js push to support React Router v5's history.push(path, state)
  // and history.push({ pathname, search, hash, state }) signatures.
  history.current.push = useCallback((to, state) => {
    _navActionRef.current = 'PUSH';
    history.current.action = 'PUSH';
    const target = resolveTargetUrl(to);
    if (isSameRouteQueryUpdate(target)) {
      setLocationState(state ?? (typeof to === 'object' ? to.state : undefined) ?? null);
      applySameRouteQueryUpdate('pushState', target);
      return;
    }
    if (typeof to === 'string') {
      setLocationState(state ?? null);
      // Disable Next's scroll handling for ordinary navigations so its
      // streaming ScrollAndFocusHandler can't override ScrollToTop's reset
      // (CON-8326). Hash navigations keep Next's scroll-to-element behaviour.
      push(to, {
        scroll: to.includes('#') ? undefined : false
      });
    } else if (to && typeof to === 'object') {
      const {
        pathname: toPathname,
        search,
        hash,
        state: toState
      } = to;
      // State can come from the object or the second argument (object form
      // takes precedence per React Router v5 behavior)
      setLocationState(state ?? toState ?? null);
      const searchWithPrefix = search && !search.startsWith('?') ? `?${search}` : search ?? '';
      push(`${toPathname ?? pathname ?? ''}${searchWithPrefix}${hash ?? ''}`, {
        scroll: hash ? undefined : false
      });
    }
  }, [pathname, push, setLocationState]);

  // Add goBack function - equivalent to window.history.back() in React Router v5
  history.current.goBack = back;

  // history.go(n) - equivalent to window.history.go(n)
  history.current.go = useCallback(n => {
    if (typeof window !== 'undefined') {
      window.history.go(n);
    }
  }, []);
  history.current.action = _navActionRef.current;
  const replaceMock = useCallback((newLocation, options = {}) => {
    if (!newLocation) {
      return;
    }
    _navActionRef.current = 'REPLACE';
    history.current.action = 'REPLACE';

    // Callers that explicitly opt in to scrolling keep the router path,
    // since the native History API never scrolls.
    const target_0 = resolveTargetUrl(newLocation);
    if (options.scroll !== true && isSameRouteQueryUpdate(target_0)) {
      applySameRouteQueryUpdate('replaceState', target_0);
      return;
    }

    // React Router v5 history.replace() never scrolls to the top of the
    // page, but Next.js router.replace() scrolls by default.  Default to
    // scroll: false to match React Router v5 semantics. Callers can still
    // opt-in via { scroll: true } when explicit scrolling is desired.
    const replaceOptions = {
      scroll: options.scroll ?? false
    };
    if (typeof newLocation === 'string') {
      replace(newLocation, replaceOptions);
    } else {
      const {
        pathname: newPathname,
        search: search_0
      } = newLocation;
      const searchWithPrefix_0 = search_0 && !search_0?.startsWith('?') ? `?${search_0}` : search_0 ?? '';
      replace(`${newPathname ?? pathname ?? ''}${searchWithPrefix_0}`, replaceOptions);
    }
  }, [pathname, replace]);
  history.current.replace = replaceMock;
  const listen = useCallback(callbackFn => {
    'use client';

    const hash_0 = window?.location?.hash;
    const handler = () => {
      if (!hash_0) {
        callbackFn();
      }
    };
    window.addEventListener('hashchange', handler);

    // Return an unlisten function for cleanup, matching React Router v5 API
    return () => {
      window.removeEventListener('hashchange', handler);
    };
  }, []);
  history.current.listen = listen;
  return history.current;
};
export default useHistoryMock;